import React, { useState, useEffect } from "react";
import {
  ArrowUpCircle,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  FileCode,
} from "lucide-react";
import { PrimaryButton, SecondaryButton } from "../common/ActionButtons";

const UpgradeN8N = () => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [currentVersion, setCurrentVersion] = useState("Loading...");
  const [newVersion, setNewVersion] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [upgradeLog, setUpgradeLog] = useState([]);
  const [lastUpgrade, setLastUpgrade] = useState(null);

  // Server configuration
  const [config, setConfig] = useState({
    server: "78.138.51.30",
    port: 22,
    user: "root",
    remoteDir: "/root/n8n-stack",
    dockerfilePath: "/root/n8n-stack/Dockerfile",
    upgradeScript: "upgrade.sh",
  });

  // Fetch current version on component mount
  useEffect(() => {
    fetchCurrentVersion();
  }, []);

  const addLog = (message, type = "info") => {
    const timestamp = new Date().toLocaleTimeString();
    setUpgradeLog((prev) => [...prev, { timestamp, message, type }]);
  };

  const fetchCurrentVersion = async () => {
    try {
      addLog("Fetching current n8n version...", "info");

      // TODO: Call backend to read Dockerfile from server
      // For now, set a placeholder
      // const response = await fetch('http://localhost:3001/api/n8n/upgrade/current-version', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ ...config, password })
      // });
      // const data = await response.json();
      // setCurrentVersion(data.version);

      setCurrentVersion("1.119.1"); // Placeholder
      addLog("Current version: 1.119.1", "info");
    } catch (error) {
      console.error("Failed to fetch version:", error);
      setCurrentVersion("Unknown");
      addLog("Failed to fetch current version", "error");
    }
  };

  const updateDockerfile = async () => {
    if (!password) {
      addLog("Password is required", "error");
      alert("Please enter the SSH password");
      return;
    }

    if (!newVersion) {
      addLog("Version is required", "error");
      alert("Please enter the new n8n version");
      return;
    }

    setIsRunning(true);
    setUpgradeLog([]);
    addLog("Starting Dockerfile update...", "info");

    try {
      const payload = {
        password: password,
        server: config.server,
        port: config.port,
        user: config.user,
        dockerfilePath: config.dockerfilePath,
        newVersion: newVersion,
      };

      addLog(`Updating Dockerfile to version ${newVersion}...`, "info");

      const response = await fetch(
        "http://localhost:3001/api/n8n/upgrade/update-dockerfile",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error(
          `Server returned ${response.status}: ${response.statusText}`,
        );
      }

      // Read Server-Sent Events stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.substring(6));
              addLog(data.message, data.type || "info");
            } catch (e) {
              console.error("Failed to parse SSE message:", e);
            }
          }
        }
      }

      setCurrentVersion(newVersion);
      addLog(`✅ Dockerfile updated to version ${newVersion}`, "success");
    } catch (error) {
      console.error("Update failed:", error);
      addLog(`❌ Update failed: ${error.message}`, "error");

      if (error.message.includes("Failed to fetch")) {
        addLog(
          "⚠️ Cannot connect to backup server. Make sure backup-server.js is running on port 3001",
          "error",
        );
      }
    } finally {
      setIsRunning(false);
    }
  };

  const runUpgrade = async () => {
    if (!password) {
      addLog("Password is required", "error");
      alert("Please enter the SSH password");
      return;
    }

    setIsRunning(true);
    setUpgradeLog([]);
    addLog("Starting n8n upgrade...", "info");

    try {
      const payload = {
        password: password,
        server: config.server,
        port: config.port,
        user: config.user,
        remoteDir: config.remoteDir,
        upgradeScript: config.upgradeScript,
      };

      addLog("Connecting to server...", "info");

      const response = await fetch(
        "http://localhost:3001/api/n8n/upgrade/run",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error(
          `Server returned ${response.status}: ${response.statusText}`,
        );
      }

      // Read Server-Sent Events stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.substring(6));
              addLog(data.message, data.type || "info");

              if (
                data.type === "success" &&
                data.message.includes("UPGRADE COMPLETED")
              ) {
                const upgradeInfo = {
                  timestamp: new Date().toISOString(),
                  version: newVersion || currentVersion,
                  server: config.server,
                };
                setLastUpgrade(upgradeInfo);
              }
            } catch (e) {
              console.error("Failed to parse SSE message:", e);
            }
          }
        }
      }

      addLog("Upgrade process completed", "info");
    } catch (error) {
      console.error("Upgrade failed:", error);
      addLog(`❌ Upgrade failed: ${error.message}`, "error");

      if (error.message.includes("Failed to fetch")) {
        addLog(
          "⚠️ Cannot connect to backup server. Make sure backup-server.js is running on port 3001",
          "error",
        );
      }
    } finally {
      setIsRunning(false);
      setPassword("");
    }
  };

  const getLogColor = (type) => {
    switch (type) {
      case "success":
        return "text-green-700";
      case "error":
        return "text-red-700";
      case "warning":
        return "text-amber-700";
      default:
        return "text-slate-700";
    }
  };

  const getLogIcon = (type) => {
    switch (type) {
      case "success":
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case "error":
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      case "warning":
        return <AlertCircle className="w-4 h-4 text-amber-600" />;
      default:
        return <div className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            N8N Upgrade Manager
          </h1>
          <p className="text-slate-600">
            Upgrade your n8n instance to the latest version
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ArrowUpCircle className="w-8 h-8 text-blue-600" />
        </div>
      </div>

      {/* Current Version Display */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <FileCode className="w-5 h-5 text-blue-600" />
          <div>
            <h3 className="text-sm font-semibold text-blue-900">
              Current n8n Version
            </h3>
            <p className="text-lg font-mono font-bold text-blue-700">
              {currentVersion}
            </p>
          </div>
          <button
            onClick={fetchCurrentVersion}
            disabled={isRunning}
            className="ml-auto text-blue-600 hover:text-blue-800 disabled:opacity-50"
            title="Refresh version"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Configuration Panel */}
      <div className="bg-white rounded-lg shadow-md border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4">
          Upgrade Configuration
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Server *
            </label>
            <input
              type="text"
              value={config.server}
              onChange={(e) => setConfig({ ...config, server: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={isRunning}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Port
            </label>
            <input
              type="number"
              value={config.port}
              onChange={(e) =>
                setConfig({ ...config, port: parseInt(e.target.value) })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={isRunning}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              User
            </label>
            <input
              type="text"
              value={config.user}
              onChange={(e) => setConfig({ ...config, user: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={isRunning}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Remote Directory
            </label>
            <input
              type="text"
              value={config.remoteDir}
              onChange={(e) =>
                setConfig({ ...config, remoteDir: e.target.value })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={isRunning}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Dockerfile Path
            </label>
            <input
              type="text"
              value={config.dockerfilePath}
              onChange={(e) =>
                setConfig({ ...config, dockerfilePath: e.target.value })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={isRunning}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Upgrade Script
            </label>
            <input
              type="text"
              value={config.upgradeScript}
              onChange={(e) =>
                setConfig({ ...config, upgradeScript: e.target.value })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={isRunning}
            />
          </div>
        </div>

        {/* New Version Input */}
        <div className="border-t border-slate-200 pt-4 mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">
            New n8n Version *
            <span className="text-xs text-slate-500 ml-2">
              (e.g., 1.120.0, 1.121.0)
            </span>
          </label>
          <input
            type="text"
            value={newVersion}
            onChange={(e) => setNewVersion(e.target.value)}
            placeholder="Enter version number (e.g., 1.120.0)"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            disabled={isRunning}
          />
          <p className="text-xs text-slate-500 mt-1">
            Check available versions at{" "}
            <a
              href="https://hub.docker.com/r/n8nio/n8n/tags"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Docker Hub
            </a>
          </p>
        </div>

        {/* Password Input */}
        <div className="border-t border-slate-200 pt-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">
            SSH Password *
          </label>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter SSH password"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 pr-10"
                disabled={isRunning}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            <PrimaryButton
              onClick={updateDockerfile}
              disabled={isRunning || !password || !newVersion}
              icon={FileCode}
            >
              Update Dockerfile
            </PrimaryButton>
            <PrimaryButton
              onClick={runUpgrade}
              disabled={isRunning || !password}
              icon={isRunning ? RefreshCw : ArrowUpCircle}
            >
              {isRunning ? "Running..." : "Run Upgrade"}
            </PrimaryButton>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Password is required for SSH authentication. It will not be stored.
          </p>
        </div>
      </div>

      {/* Last Upgrade Info */}
      {lastUpgrade && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-green-900 mb-1">
                Last Upgrade Completed
              </h3>
              <p className="text-sm text-green-800">
                <strong>Time:</strong>{" "}
                {new Date(lastUpgrade.timestamp).toLocaleString()}
              </p>
              <p className="text-sm text-green-800">
                <strong>Version:</strong> {lastUpgrade.version}
              </p>
              <p className="text-sm text-green-800">
                <strong>Server:</strong> {lastUpgrade.server}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Log */}
      <div className="bg-white rounded-lg shadow-md border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-800">Upgrade Log</h3>
          {upgradeLog.length > 0 && (
            <SecondaryButton
              onClick={() => setUpgradeLog([])}
              disabled={isRunning}
            >
              Clear Log
            </SecondaryButton>
          )}
        </div>
        <div className="p-4 bg-slate-900 rounded-b-lg min-h-[300px] max-h-[500px] overflow-y-auto font-mono text-sm">
          {upgradeLog.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <ArrowUpCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>
                No upgrade running. Update Dockerfile or click "Run Upgrade" to
                begin.
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {upgradeLog.map((log, index) => (
                <div
                  key={index}
                  className="flex items-start gap-2 text-slate-300"
                >
                  <span className="text-slate-500 shrink-0">
                    [{log.timestamp}]
                  </span>
                  {getLogIcon(log.type)}
                  <span className={getLogColor(log.type)}>{log.message}</span>
                </div>
              ))}
              {isRunning && (
                <div className="flex items-center gap-2 text-blue-400 animate-pulse">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Processing...</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Information Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">
            📝 Upgrade Process
          </h3>
          <ul className="text-sm text-blue-800 space-y-1 ml-4 list-decimal">
            <li>Enter new n8n version number</li>
            <li>Click "Update Dockerfile" to update ARG N8N_VERSION</li>
            <li>Click "Run Upgrade" to execute upgrade.sh</li>
            <li>upgrade.sh pulls base images from Docker Hub</li>
            <li>upgrade.sh rebuilds custom Dockerfile (important!)</li>
            <li>Containers restart with new version</li>
            <li>Verify n8n is running and check version</li>
          </ul>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-amber-900 mb-2">
            ⚙️ What upgrade.sh Does
          </h3>
          <ul className="text-sm text-amber-800 space-y-1 ml-4 list-disc">
            <li>Pull base images (docker compose pull)</li>
            <li>Rebuild custom Dockerfile (docker compose build)</li>
            <li>Stop all containers gracefully</li>
            <li>Start containers with new images</li>
            <li>Verify containers are running</li>
            <li>Check new n8n version</li>
          </ul>
        </div>

        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-purple-900 mb-2">
            🔒 Safety Tips
          </h3>
          <ul className="text-sm text-purple-800 space-y-1 ml-4 list-disc">
            <li>Always backup before upgrading!</li>
            <li>Check n8n release notes first</li>
            <li>Test in staging environment</li>
            <li>Keep previous Dockerfile as backup</li>
            <li>Monitor logs during upgrade</li>
          </ul>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-green-900 mb-2">
            🎯 Dockerfile Update
          </h3>
          <p className="text-sm text-green-800 mb-2">Updates this line:</p>
          <code className="text-xs bg-green-100 px-2 py-1 rounded block">
            ARG N8N_VERSION=1.119.1
          </code>
          <p className="text-sm text-green-800 mt-2">
            To your specified version:
          </p>
          <code className="text-xs bg-green-100 px-2 py-1 rounded block">
            ARG N8N_VERSION={newVersion || "X.XXX.X"}
          </code>
        </div>
      </div>
    </div>
  );
};

export default UpgradeN8N;
